/*
 * 模块: DataAnalysisTabs
 * 职责: 数据分析标签页组织
 * 依赖: antd, SKUDetail子组件
 * 导出: DataAnalysisTabs
 * 限制: 文件<150行
 */

import React from 'react';
import { Tabs, Button, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { HotSearchTabs } from './DataAnalysisTabs/HotSearchTabs';
import { SearchPerformanceTabs } from './DataAnalysisTabs/SearchPerformanceTabs';
import type {
  AdvantageKeywordsResult,
  MarketOverview,
  OpportunityKeywordsResult,
} from '../../types';

interface DataAnalysisTabsProps {
  periodsCount: number;
  keywords: any[];
  competitors: any[];
  networkData: any;
  brandDetails: any;
  networkLoading: boolean;
  brandDetailsLoading: boolean;
  matrixData: any;
  rankRangeData: any;
  brandShiftData: any;
  matrixLoading: boolean;
  onTabChange: (key: string) => void;
  onExport: (type: 'excel' | 'pdf') => void;
  onExportEnhanced: () => void;
  onGenerateNetwork: () => void;
  onRefreshBrandDetails: () => void;
  // 搜索查询绩效数据
  spPeriodsCount: number;
  spSelectedPeriodId: number | null;
  advantageData: AdvantageKeywordsResult | null;
  marketOverview: MarketOverview | null;
  opportunityData: OpportunityKeywordsResult | null;
  keywordPivotData: any | null;
  spLoading: boolean;
}

/**
 * 数据分析标签页组件
 * 组织和展示各类数据分析Tab
 */
export const DataAnalysisTabs: React.FC<DataAnalysisTabsProps> = ({
  periodsCount,
  keywords,
  competitors,
  networkData,
  brandDetails,
  networkLoading,
  brandDetailsLoading,
  matrixData,
  rankRangeData,
  brandShiftData,
  matrixLoading,
  onTabChange,
  onExport,
  onExportEnhanced,
  onGenerateNetwork,
  onRefreshBrandDetails,
  // 搜索查询绩效数据
  spPeriodsCount,
  spSelectedPeriodId,
  advantageData,
  marketOverview,
  opportunityData,
  keywordPivotData,
  spLoading,
}) => {
  return (
    <div className="data-analysis-tabs" style={{ padding: '0 32px 32px' }}>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ fontSize: 20, fontWeight: 600, color: '#0f172a', margin: 0 }}>
          数据分析
          <span style={{ fontSize: 14, fontWeight: 400, color: '#64748b', marginLeft: 12 }}>
            (热门搜索词: {periodsCount} 个周期 | 搜索查询绩效: {spPeriodsCount} 个周期)
          </span>
        </h3>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => onExport('excel')}>
            导出Excel
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => onExport('pdf')}>
            导出PDF
          </Button>
        </Space>
      </div>

      <div style={{
        background: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(24px)',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 10px 15px -3px rgba(0, 0, 0, 0.02), inset 0 0 0 1px rgba(255, 255, 255, 0.6)'
      }}>
        {/* 一级Tab：数据源分类 */}
        <Tabs
          defaultActiveKey="hot-search"
          type="card"
          className="premium-tabs"
          items={[
            // ==================== 热门搜索词数据 ====================
            {
              key: 'hot-search',
              label: `📊 热门搜索词 (${periodsCount})`,
              children: (
                <HotSearchTabs
                  keywords={keywords}
                  competitors={competitors}
                  networkData={networkData}
                  brandDetails={brandDetails}
                  networkLoading={networkLoading}
                  brandDetailsLoading={brandDetailsLoading}
                  matrixData={matrixData}
                  rankRangeData={rankRangeData}
                  brandShiftData={brandShiftData}
                  matrixLoading={matrixLoading}
                  onTabChange={onTabChange}
                  onGenerateNetwork={onGenerateNetwork}
                  onRefreshBrandDetails={onRefreshBrandDetails}
                  onExportEnhanced={onExportEnhanced}
                />
              ),
            },
            // ==================== 搜索查询绩效数据 ====================
            ...(spPeriodsCount > 0
              ? [
                {
                  key: 'search-performance',
                  label: `🔍 搜索查询绩效 (${spPeriodsCount})`,
                  children: (
                    <SearchPerformanceTabs
                      keywordPivotData={keywordPivotData}
                      spLoading={spLoading}
                      spSelectedPeriodId={spSelectedPeriodId}
                      advantageData={advantageData}
                      marketOverview={marketOverview}
                      opportunityData={opportunityData}
                      onTabChange={onTabChange}
                    />
                  ),
                },
              ]
              : []),
          ]}
        />
      </div>
    </div>
  );
};
